import { useEffect, useMemo, useState } from 'react';

import { useLazyQuery, useQuery } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { format } from 'date-fns';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { useAppSelector } from '@/app/hooks';
import { addToast } from '@/app/slices/toast';
import {
  useAddPregnantStatus,
  useAspirationByDonorDateSite,
  useAspirationHistory,
  useDonorScheduleData,
  useFertDataByDonorId,
  useProceduresByDonorId,
  useUpdateAspirationChuteside,
  useUpdateDonorLastBred,
} from '@/services/aspiration/hooks';
import type { AspirationDataEntryRequest } from '@/services/aspiration/types';
import {
  GET_ASPIRATION_BY_ID,
  GET_ASPIRATION_INFO_BY_ID,
  GET_SCHEDULES,
  GET_VETERINARIANS,
} from '@/types.gql';
import { formatDateWithPattern, formatTime } from '@/utils';

import { calculateLastBredDate, setMinute } from '../../utils';
import {
  AspirationFormValues,
  useAspirationFormSchema,
} from '../../utils/schema';
import { useAspirationContext } from '../useAspirationContext';

export const useAspirationForm = (setShowForm: (v: boolean) => void) => {
  const dispatch = useDispatch();
  const { me } = useAppSelector((s) => s.authentication);
  const mode = useAppSelector((s) => s.preferences.mode);
  const aspirationFormSchema = useAspirationFormSchema();

  const [params, setParams] = useSearchParams();
  const {
    selectedAspiration,
    aspSite,
    refetchTodaysAspirations,
    setQuickSearchValue,
  } = useAspirationContext();

  const opuDate =
    params.get('d') || formatDateWithPattern(new Date(), 'MM-dd-yyyy');
  const opuSiteId = (params.get('sId') as string) || '';
  const opuTechnicianId = (params.get('otId') as string) || '';
  const chuteNumber = (params.get('chuteNumber') as string) || '';
  const aspirationId = (params.get('aId') as string) || '';
  const timeZone = (params.get('tz') as string) || '';

  const [ovarySideFirst] = useState(
    () =>
      localStorage.getItem('ovarySideFirst') ||
      localStorage.getItem('nucleus|ovarySideFirst') ||
      'Left',
  );
  const [showAspirationStats, setShowAspirationStats] = useState(false);
  const [currentPregnantStatus, setCurrentPregnantStatus] = useState<
    boolean | null
  >(null);

  const { mutateAsync: updateAspirationChuteside, isPending: saving } =
    useUpdateAspirationChuteside();
  const { mutateAsync: setPregnantStatus } = useAddPregnantStatus();
  const { mutateAsync: updateDonorLastBredDate } = useUpdateDonorLastBred();

  const [
    getAspirationById,
    {
      data: aspirationInfoData,
      loading: aspirationInfoLoading,
      refetch: refetchAspirationInfo,
    },
  ] = useLazyQuery(GET_ASPIRATION_BY_ID);

  const aspirationInfo = aspirationInfoData?.aspirations?.items?.[0];

  const { data: aspirationInfoByIdData } = useQuery(GET_ASPIRATION_INFO_BY_ID, {
    variables: { aspirationId },
    skip: !aspirationId,
  });
  const donorCategoryAndBodyCondition =
    aspirationInfoByIdData?.aspirations?.items?.[0];

  const scheduleOptions =
    useQuery(GET_SCHEDULES)?.data?.scheduleMaster?.items?.map((item) => ({
      label: item?.name ?? '',
      value: String(item?.id ?? ''),
    })) ?? [];

  const { data: fertData } = useFertDataByDonorId({
    donorId: aspirationInfo?.donorId as string | null,
    ranch: opuSiteId,
  });

  const { data: proceduresData } = useProceduresByDonorId({
    donorId: aspirationInfo?.donorId as string | null,
    ranch: opuSiteId,
  });

  const { data: aspirationHistoryData } = useAspirationHistory({
    donorId: aspirationInfo?.donorId as string | null,
    aspirationDate: opuDate,
    ranch: opuSiteId,
  });

  const { data: donorScheduleData } = useDonorScheduleData({
    donorId: aspirationInfo?.donorId as string | null,
    aspirationDate: opuDate,
    ranch: opuSiteId,
  });

  const opuTechnicianData =
    useQuery(GET_VETERINARIANS)?.data?.equipments?.items;

  const { data: donorSiteData, isLoading: donorSiteLoading } =
    useAspirationByDonorDateSite({
      donorId: aspirationInfo?.donorId as string | null,
      date: opuDate,
      siteId: opuSiteId,
    });

  const chuteTechId =
    (donorSiteData as any)?.[0]?.asstId ?? aspirationInfo?.assistant?.id ?? '';

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    reset,
    setError,
    watch,
    formState: { errors },
  } = useForm<AspirationFormValues>({
    resolver: zodResolver(aspirationFormSchema),
    defaultValues: {
      leftLarge: 0,
      leftMedium: 0,
      leftSmall: 0,
      rightLarge: 0,
      rightMedium: 0,
      rightSmall: 0,
      leftTotal: 0,
      rightTotal: 0,
      comments: '',
      leftCls: 0,
      leftCysts: 0,
      rightCls: 0,
      rightCysts: 0,
      opuTime: '',
      didNotAspirate: false,
      donorCondition: '',
      donorConditionChanged: false,
      pregnant: false,
      weight: '',
      nextScheduleId: '',
      weightIn: '',
      cdComments: '',
    },
  });

  const leftLarge = watch('leftLarge');
  const leftMedium = watch('leftMedium');
  const leftSmall = watch('leftSmall');
  const rightLarge = watch('rightLarge');
  const rightMedium = watch('rightMedium');
  const rightSmall = watch('rightSmall');

  const leftTotal = (leftLarge || 0) + (leftMedium || 0) + (leftSmall || 0);
  const rightTotal = (rightLarge || 0) + (rightMedium || 0) + (rightSmall || 0);

  const donorCondition = watch('donorCondition');

  const months = useMemo(() => {
    return aspirationInfo?.donor?.birth
      ? Math.floor(
          (new Date().getTime() -
            new Date(aspirationInfo?.donor?.birth as string).getTime()) /
            ((365.25 / 12) * 24 * 60 * 60 * 1000),
        )
      : null;
  }, [aspirationInfo?.donor?.birth]);

  const lastBredInterval = useMemo(() => {
    const daysDifference = Math.floor(
      (new Date().getTime() -
        new Date(aspirationInfo?.donor?.lastBred as string).getTime()) /
        (24 * 60 * 60 * 1000),
    );
    if (selectedAspiration?.pregnant?.toString() === '100000001') {
      return `(Pregnant: ${daysDifference} days)`;
    }
    return daysDifference < 30 ? 'Bred in last 30 days - DO NOT ESTRUMATE' : '';
  }, [aspirationInfo?.donor?.lastBred, selectedAspiration?.pregnant]);

  const donorId = aspirationInfo?.donorId ?? '';
  const scheduleId = aspirationInfo?.scheduleMasterId ?? '';
  const opuTechId = opuTechnicianId ?? '';

  const readyToSave = Boolean(
    aspirationId && opuDate && opuSiteId && opuTechId && donorId && scheduleId,
  );
  const saveBlockedReason =
    (!aspirationId && 'Missing aspiration ID') ||
    (!opuDate && 'Missing aspiration date') ||
    (!opuSiteId && 'Missing site') ||
    (!opuTechId && 'Missing technician (otId)') ||
    (!donorId && 'Loading donor info…') ||
    (!scheduleId && 'Loading schedule…') ||
    '';

  const formDataLoading = Boolean(
    aspirationId && !aspirationInfoData && aspirationInfoLoading,
  );

  useMemo(() => {
    if (selectedAspiration?.pregnant) {
      setCurrentPregnantStatus(selectedAspiration?.pregnant === '100000001');
    }
  }, [selectedAspiration?.pregnant]);

  useEffect(() => {
    if (aspirationId) {
      getAspirationById({ variables: { aspirationId } });
    }
  }, [aspirationId, getAspirationById]);

  useEffect(() => {
    if ((aspirationInfo?.donor?.condition || '') !== (donorCondition || '')) {
      setValue('donorConditionChanged', true);
    } else {
      setValue('donorConditionChanged', false);
    }
  }, [donorCondition, setValue, aspirationInfo?.donor?.condition]);

  useEffect(() => {
    if (selectedAspiration && aspirationInfoData) {
      const formData = {
        leftLarge: aspirationInfo?.nofollll || 0,
        leftMedium: aspirationInfo?.nofolllm || 0,
        leftSmall: aspirationInfo?.nofollls || 0,
        rightLarge: aspirationInfo?.nofollrl || 0,
        rightMedium: aspirationInfo?.nofollrm || 0,
        rightSmall: aspirationInfo?.nofollrs || 0,
        leftTotal: aspirationInfo?.totfolll || 0,
        rightTotal: aspirationInfo?.totfollr || 0,
        comments: aspirationInfo?.comment || '',
        leftCls: aspirationInfo?.cll || 0,
        leftCysts: parseInt(aspirationInfo?.cystl as string) || 0,
        rightCls: aspirationInfo?.clr || 0,
        rightCysts: parseInt(aspirationInfo?.cystr as string) || 0,
        opuTime: formatTime(new Date().toISOString()),
        didNotAspirate: aspirationInfo?.fertPlan === 'DID_NOT_ASPIRATE',
        donorCondition: aspirationInfo?.donor?.condition as string,
        donorConditionChanged: false,
        weight: (selectedAspiration?.weight as string) || '',
        weightIn: (selectedAspiration?.weight as string) || '',
        nextScheduleId: selectedAspiration?.nextScheduleId || '',
        comment: aspirationInfo?.comment || '',
        pregnant: selectedAspiration?.pregnant === '100000001',
        bodyConditionScore: donorCategoryAndBodyCondition?.bodyConditionScore,
        donorCategory: donorCategoryAndBodyCondition?.donorCategory,
      };
      reset(formData);
    }
  }, [
    aspirationInfo,
    aspirationInfoData,
    reset,
    selectedAspiration,
    timeZone,
    donorCategoryAndBodyCondition,
  ]);

  useEffect(() => {
    setMinute(setValue);
  }, [setValue]);

  const onSubmit = async (data: AspirationFormValues) => {
    if (!readyToSave) {
      dispatch(
        addToast({
          severity: 'warn',
          summary: 'Not ready to save',
          detail: saveBlockedReason || 'Still loading required data.',
          sticky: false,
          closable: true,
        }),
      );
      return;
    }

    const input: AspirationDataEntryRequest = {
      aspirationId,
      siteId: opuSiteId,
      aspirationDate: format(new Date(opuDate), 'yyyy-MM-dd'),
      vetId: opuTechId,
      techId: chuteTechId,
      donorId,
      scheduleId,
      opuTime: data.opuTime,
      didNotAspirate: data.didNotAspirate,
      leftLarge: data.leftLarge,
      leftMedium: data.leftMedium,
      leftSmall: data.leftSmall,
      leftTotal,
      leftCls: data.leftCls,
      leftCysts: data.leftCysts,
      rightLarge: data.rightLarge,
      rightMedium: data.rightMedium,
      rightSmall: data.rightSmall,
      rightTotal,
      rightCls: data.rightCls,
      rightCysts: data.rightCysts,
      weight: data.weight,
      nextScheduleId: data.nextScheduleId,
      weightIn: data.weightIn,
      donorCondition: data.donorCondition || null,
      donorConditionChanged: data.donorConditionChanged ?? false,
      comment: data.comments,
      cdComments: data.cdComments,
      pregnant: data.pregnant,
      chuteNumber: chuteNumber === '2' ? 100000001 : 100000000,
      bodyConditionScore: data?.bodyConditionScore,
      donorCategory: data?.donorCategory,
      location: params.get('l') || '',
    };

    try {
      const result = await updateAspirationChuteside(input, 'false');
      if (result.success) {
        setQuickSearchValue('');

        if (data?.pregnant !== currentPregnantStatus) {
          try {
            await setPregnantStatus({
              pregnantValue: data.pregnant ? 100000001 : 100000000,
              donorId,
              opuTechId,
              chuteTechId: chuteTechId || '',
            });
            setCurrentPregnantStatus(data.pregnant);
          } catch (error) {
            dispatch(
              addToast({
                severity: 'error',
                summary: 'Error',
                detail: 'Pregnant status not set: ' + error,
                sticky: false,
                closable: true,
              }),
            );
          }
        }

        if (data.comments && donorId) {
          const commentsArray = data.comments.split('. ').map((c) => ({
            comment: c.trim(),
          }));
          const pregnantComment = commentsArray.find((c) =>
            c.comment.toLowerCase().includes('pregnant'),
          );

          if (pregnantComment) {
            const daysMatch = pregnantComment.comment.match(/: (\d+) days?/i);
            if (daysMatch) {
              const daysPregnant = parseInt(daysMatch[1], 10);
              if (!Number.isNaN(daysPregnant) && daysPregnant > 0) {
                const lastBredDate = calculateLastBredDate(
                  opuDate,
                  daysPregnant,
                );

                try {
                  const lastBredDateString = lastBredDate
                    .toISOString()
                    .slice(0, 19);
                  await updateDonorLastBredDate(donorId, lastBredDateString);
                  refetchAspirationInfo();
                } catch (error) {
                  dispatch(
                    addToast({
                      severity: 'error',
                      summary: 'Error',
                      detail: 'Failed to update donor last bred date: ' + error,
                      sticky: false,
                      closable: true,
                    }),
                  );
                }
              }
            }
          }
        }

        const newSearchParams = new URLSearchParams(params);
        newSearchParams.delete('aId');
        newSearchParams.delete('tz');
        const keep = ['sId', 'd', 'otId', 'l'];
        for (const [key] of Array.from(newSearchParams.keys())) {
          if (!keep.includes(key)) newSearchParams.delete(key);
        }
        setParams(newSearchParams);

        refetchTodaysAspirations();
        refetchAspirationInfo();
        setShowForm(false);
        dispatch(
          addToast({
            severity: 'success',
            summary: 'Success',
            detail: 'Aspiration saved successfully',
            sticky: false,
            closable: true,
          }),
        );
      } else {
        dispatch(
          addToast({
            severity: 'error',
            summary: 'Error',
            detail: 'Aspiration not saved: ' + (result.message ?? ''),
            sticky: false,
            closable: true,
          }),
        );
      }
    } catch (error: any) {
      const message =
        error?.graphQLErrors?.[0]?.message ||
        error?.message ||
        'An error occurred while saving the aspiration. Please try again.';
      setError('root', { type: 'manual', message });
      dispatch(
        addToast({
          severity: 'error',
          summary: 'Error',
          detail: message,
          sticky: false,
          closable: true,
        }),
      );
    }
  };

  const handleCancel = () => {
    const newSearchParams = new URLSearchParams(params);
    newSearchParams.delete('aId');
    newSearchParams.delete('tz');
    setParams(newSearchParams);
    setQuickSearchValue('');
    setShowForm(false);
  };

  const isBrazil =
    me?.environmentId === 'brazil' ||
    (window as any)?.ENVIRONMENT_ID_VALUES?.Brazil === me?.environmentId;

  return {
    // env / ui
    mode,
    me,
    isBrazil: Boolean(isBrazil),

    // params
    opuDate,
    opuSiteId,
    opuTechnicianId,
    aspirationId,
    chuteNumber,
    timeZone,

    // context / fetched
    selectedAspiration,
    aspSite,
    aspirationInfo,
    opuTechnicianData,
    scheduleOptions,
    fertData,
    proceduresData,
    aspirationHistoryData,
    donorScheduleData,
    chuteTechId,
    donorSiteLoading,

    // form
    control,
    errors,
    handleSubmit,
    onSubmit,
    handleCancel,
    setValue,
    getValues,
    leftTotal,
    rightTotal,

    // derived display
    months,
    lastBredInterval,
    ovarySideFirst,

    // save state
    readyToSave,
    saveBlockedReason,
    saving: saving || donorSiteLoading,
    aspirationInfoLoading,
    formDataLoading,

    showAspirationStats,
    setShowAspirationStats,
  };
};
